import { useEffect, useRef, useState } from "react";
import { FormControl, InputGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useOL } from "rlayers";
import { fromLonLat } from "ol/proj";
import useClientConfig from "../../common/hooks/clientConfigHook";
import { Feature } from "ol";
import { Fill, Icon, Style, Text } from "ol/style";
import { Vector } from "ol/layer";
import VectorSource from "ol/source/Vector";
import { Point } from "ol/geom";
import { DefaultButton } from "../../common/components/DefaultButton";
import useNetwork from "../../common/hooks/networkHook";
import { useSelectedJobid } from "../../utils/CADStateProvider";
import { altCoord } from "../../utils/geoUtilities";

const GeoSearch = ({ style, onSelect, onJobCreate }) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const network = useNetwork();
    const [showQuery, setShowQuery] = useState(false)
    const [dirty, setDirty] = useState(false);
    const [results, setResults] = useState([]);
    const [selectedIdx, setSelectedIdx] = useState(0);
    const [query, setQuery] = useState();
    const { selectedJobId, setSelectedJobId } = useSelectedJobid();

    const processSearchKey = async event => {
        if (event.keyCode === 13) {
            if (dirty) {
                try {
                    setDirty(false);
                    let response = await network.fetchData('/api/v1/geoSearch?q=' + encodeURIComponent(query));
                    if (response?.ok) {
                        setResults(await response.json());
                    }
                } catch (err) {
                    console.log(err);
                }
            } else {
                doSelect(results[selectedIdx]);
            }
            event.stopPropagation();
            event.preventDefault();
        } else if (results?.length > 0) {
            if (event.keyCode === 40 && selectedIdx < results.length - 1) {
                setSelectedIdx(selectedIdx + 1);
                event.stopPropagation();
                event.preventDefault();
            } else if (event.keyCode === 38 && selectedIdx > 0) {
                setSelectedIdx(selectedIdx - 1);
                event.stopPropagation();
                event.preventDefault();
            }
        }
    }

    const doSelect = res => {
        onSelect(res);
        setResults([]);
        setQuery('');
        setShowQuery(false);
    }

    const createJob = async res => {
        let locationName = res.display_name;
        if (res.address) {
            if (locationName) {
                locationName += ', ' + res.address;
            } else {
                locationName = res.address;
            }
        }
        if (clientConfig.altcoordMode !== 'none') {
            let coord = await altCoord(network, res.lat, res.lon)
            if (coord?.addr) {
                res.w3w = coord.addr;
            } 
        }

        if (selectedJobId) {
            setSelectedJobId();
        }
        onJobCreate({ latitude: res.lat, longitude: res.lon, location: locationName, w3w: res.w3w })
        setResults([]);
        setQuery('');
        setShowQuery(false);
    }

    return <div style={{maxWidth: '250px', ...style, zIndex: 100}}>
        <InputGroup>
            <InputGroup.Prepend>
                <DefaultButton kind="search" variant="solid-primary" style={{ width: '38px' }} hideLabel={true} onMouseEnter={() => setShowQuery(true)} onClick={() => { if (showQuery) { setQuery(null); setResults([]); setSelectedIdx(0); } setShowQuery(!showQuery) }}></DefaultButton>
            </InputGroup.Prepend>
            { showQuery ? <FormControl type="text" value={query} placeholder={t('search')} onKeyDown={processSearchKey} onChange={e => { setDirty(true); setQuery(e.target.value); }}></FormControl> : null }
        </InputGroup>
        {results?.length > 0 ? <div className="mt-2 d-flex" style={{ flexDirection: 'column', justifyItems: 'stretch', width: 'calc(80vw + 40px)', maxWidth: '400px', maxHeight: 'calc(30dvh)', marginTop: '4px', overflowY: 'auto', backgroundColor: 'white', zIndex: 100}}>
            {results.map((res, idx) => <div className="w-100 p-2 d-flex" onMouseEnter={() => setSelectedIdx(idx)} onClick={() => doSelect(res)} style={{ flexDirection: 'row', justifyItems: 'stretch', borderBottom: '1px solid black', backgroundColor: idx === selectedIdx ? 'lightgrey' : null, cursor: 'pointer' }}>
                <div style={{ flex: 1 }}>
                    { res.display_name && res.address 
                        ? <><span style={{ fontWeight: 'bold' }}>{res.display_name}</span><br/><span style={{ fontStyle: 'italic', fontSize: 'small', color: 'grey' }}>{res.address}</span></> 
                        : <span>{res.display_name ?? res.address}</span> }
                </div>
                { onJobCreate ? <div>
                    <DefaultButton kind="new" hideLabel label={t('newJob')} variant="outline-dark" onClick={(event) => { event.preventDefault(); event.stopPropagation(); createJob(res); }}></DefaultButton>
                </div> : null }
            </div>)}
        </div> : null}
    </div>;
}

export const RGeoSearch = ({ style, onJobCreate }) => {
    const clientConfig = useClientConfig();
    const iconLayer = useRef();
    const { map } = useOL();

    useEffect(() => {
        iconLayer.current = new Vector({ source: new VectorSource({ features: []})});
        iconLayer.current.setZIndex(4001);
        map.addLayer(iconLayer.current);
    }, [map]);

    const selectEntry = entry => {
        map.getView().fit(fromLonLat([
            entry.extent.xmin,
            entry.extent.ymin
        ]).concat(fromLonLat([
            entry.extent.xmax,
            entry.extent.ymax
        ])), { maxZoom: 18 });
        const isDark = clientConfig.mapTiles?.mode === 'advanced' && map.getAllLayers().filter(l => l.get('mapbox-source')).flatMap(l => l.getSource().getUrls()).some(url => ['ArcGIS:Imagery', 'ArcGIS:NavigationNight', 'OSM:DarkGrey'].some(str => url.indexOf(str) !== -1));
        const icon = new Feature({
            geometry: new Point(fromLonLat([ entry.lon, entry.lat ])),
        });
        icon.setStyle(new Style({
            image: new Icon({
                src: isDark ? 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAABmJLR0QAAAAAAAD5Q7t/AAAACXBIWXMAAAsTAAALEwEAmpwYAAABG0lEQVRo3u1ZUQ7FIAgDsvtf2ff7ssyJUIEZ+V0mLZatCtGJ3GDEIq21ZkrOzGkErKDRZLgCeA8JrgDcQ4SrgZ8lwSjg94T39/6fzxRjRIS94HsJ3gigchARXdFfjd46VomKpfoo8No137BIBfAeEpIhG2QO0TKNAG9peK4GfhaP0MdDKujek1uyrAJKWntLKFM+Wgz7N3H1uKxuFC0vuJk7EjoEAD1QwcCZzgNHQlUIVPBHIwz77EAF32NpePFuYaZ8tpAQrzrUa2/mPOfhxx3wXAFGHuZNEoogMZtDkL/1leB7mATtTSLBD82cJqH3/zEqhms+MFvttyGGZcChKc7+I6YMEkuGfBFElo9ZV5IIHXSjyHzJAZ/oxQ89UshWZ5PKSAAAAABJRU5ErkJggg==' :
                            'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAAAXNSR0IArs4c6QAABNRJREFUaEPdmUeIbUUQhr+nGyNudKMuzFnEgGIGxayIiuGpIIKYEXcmRDGvRDALgmJOGDG7MKALc8KAYaOCujK7MfBduqWnXp97Tt9737wZazVzT3d1/d3VFf5ewiKXJYvcfmYFYA1gT2AvYBtgM2BdYPW0Qb8B3wBfAB8BLwOvAv4+lUwDwLkHAicDhwGrNFryJ/A4cAfwHPBP4/zR8EkBHA1cDGw7yaKVOR8AlwGPtOprBbApcDOwb+tCA8e/AJyZXG3QlBYAJwC3APp7Tb5MrvAa8CnwThi0A7BFuisHABt16PkFOA24bwiCIQAccyVwQUXhX8ADwA3AG+F79OlyLf/eFTgbOAZYuaLbNXXTsXejD4DfbwJOryzgxTsH+Lxjp8YBKKdsDlwP7FfRo7ueNQ5EH4CrgfOD4t+T4bf3HPFQAFnNqcB1wKpB7xXpJKrLjQOgz98dZn0PHFzx75ryVgDq2Al4GlgnKFwK3F9bpAuA0cZLWF7YH1Ki+mzI5aoce99pZ7W6lEmuBPEzYBAwUMyRLqUvhlCp25hpY2QZh8XMu14a8C2w/kDg+SReCe70PGD06gVgknowjDsF6PP5qPtQ4Nb0o2HxqQYADvVO5Pl56pHAo6WeeAL+/37IsCaX/RsXn9XwZ1K5kvVp2/ale0YAB6VLlCcY57caEypnZWiXni2BD0Oe0MZn84QIQNfRhbLcA5y4vK3s0W9GPq4YYzQyKo2kBGDE+TFUlbtVMux849kdsDzJ8gewNmBgmQMguo8hy3A6UZk7Q5RusrZsWOg0GhmV5gC4BjivGGQJYRpfCGIRaSTLYoVwYQTwJGDoy3L80IpwHhDGquAJ4PAIwAxrK5jFcPXePBg3ZAmz8NvFQMt1I9QcFzJdrzlA23fpOFsTU5/qnPjspftEW9eKAP5uaDFbS4M+g/xelh594w0sK/3vACx6F4qX2Ivzbt9ZztP3QZd4IYdRy5m7is2qhtHYPtqPSnEsBImJ7CrgoniJZdksX7N8BWyyQEoJbdmgsK1aStSKOQup11fwEeyRWsxshkWc7eYyxZwD5HjkabLcC5jGV6RYPh9bGGB5bZkzktgPRDeyodkaGNrIzxqozZS8aUl8aaOcVBWAgKw5rIOyrMiW8iVgn8IWazND6n8lfo2VOAp4OGylpextjds7bVN/RmIFy2WPAB4rf+iiVWwWSqrPLsjHi7caQExDq+ycHkHKNwf7YJuuOdIFwPApB1RWp7abckND78MkzJzGyWDLCZXE1k/JdQyngwA4yMbZKFSKIA4B3hxwEpMA2AWwIojUopHxodqafXSfxOoo4xXi09C5FdIp6m8FoM9fW3mq8uXmkq4N6wPg9xsBlUeRfpRe/6RD+VAAhkrp9TLaZJWu7RtCp/QBcKJjLq+chN/ME3JJLmTGLo3ue+Awy2ucPNSoOQkydufz2CEA8ljJJUNpV9v5daI6ZJY9lbKHVceOqY81EFjLlLVNabt9iVxs1ecjyhYAzt04xeblxZUaKqVylok2k96BrnkmFC/WduP8s+GbGfbS9G7cMG3yd+J8N3SFkxJHE5+G+gwxOZpV70yuNxED2OpCXUatBlj27p2KP/klHzTyC8+viXXwQfDjlGXlO0cl8TQyKwDT2DDV3EUP4F/c3OsxlHMbzgAAAABJRU5ErkJggg=='
            }),
            text: new Text({
                text: entry.display_name,
                offsetY: -32,
                font: '12px Arial',
                fill: new Fill({ color: isDark ? 'white' : 'black' }),
                overflow: true
            })
        }));
        icon.set('altitudeMode', 'clampToGround');
        iconLayer.current.getSource().addFeature(icon);
        setTimeout(() => iconLayer.current.getSource().removeFeature(icon), 60000);
    }

    return <GeoSearch style={style} onSelect={selectEntry} onJobCreate={onJobCreate}></GeoSearch>
}